<script lang="ts">
	import { browser } from '$app/environment';
	import { base } from '$app/paths';
	import { PUBLIC_RERUN_R2_BASE_URL } from '$env/static/public';
	import Button from '$lib/components/ui/button/button.svelte';
	import { debugEnabled, showInfoCard } from '$lib/stores/config';
	import { motionSamples, poseDataSource } from '$lib/data/motionSamples';

	type BenchmarkMetric = {
		label: string;
		value: string;
		detail?: string;
	};

	type BenchmarkFigure = {
		src: string;
		alt: string;
		caption: string;
	};

	type BenchmarkSection = {
		id: string;
		title: string;
		summary: string;
		citations?: string[];
		metrics: BenchmarkMetric[];
		figures: BenchmarkFigure[];
		notes?: string[];
	};

	const r2BaseUrl = PUBLIC_RERUN_R2_BASE_URL?.replace(/\/+$/, '') ?? '';
	const hasR2BaseUrl = Boolean(r2BaseUrl);

	const resolveAssetUrl = (assetUrl: string) => {
		if (assetUrl.startsWith('http')) {
			return assetUrl;
		}

		if (browser && typeof window !== 'undefined') {
			return new URL(`${base}${assetUrl}`, window.location.origin).href;
		}

		return `${base}${assetUrl}`;
	};

	const recordingUrlFor = (key: string) =>
		r2BaseUrl ? `${r2BaseUrl}/rerun/${poseDataSource}/${key}.rrd` : '';
	const rerunViewerUrl = (recordingUrl: string) =>
		`https://app.rerun.io/?url=${encodeURIComponent(resolveAssetUrl(recordingUrl))}&persist=1`;

	let activeSampleKey = motionSamples[0]?.key ?? '';

	$: activeSample = motionSamples.find((sample) => sample.key === activeSampleKey);
	$: activeRecordingUrl = activeSample ? recordingUrlFor(activeSample.key) : '';

	const citationPriority = (citation: string) => {
		if (/paper section/i.test(citation)) return 0;
		if (/table/i.test(citation)) return 1;
		if (/appendix/i.test(citation)) return 2;
		if (/figure/i.test(citation)) return 3;
		return 1;
	};

	const orderedCitations = (citations: string[] = []) =>
		[...citations].sort((a, b) => citationPriority(a) - citationPriority(b));

	const benchmarkSections: BenchmarkSection[] = [
		{
			id: 'retargeting',
			title: 'Retargeting Accuracy & Performance',
			summary: 'Evaluated on LAFAN-1 dataset (upper body motion only) on Unitree G1 robot',
			citations: ['Paper Section VI-A', 'Figure 5'],
			metrics: [
				{
					label: 'Median alignment error',
					value: '1.57e-13',
					detail: 'IQR: [1.06e-13, 1.66e-5]'
				},
				{
					label: 'Median inference time',
					value: '0.587 ms',
					detail: 'IQR: [0.573, 0.612] ms'
				},
				{
					label: 'Relative speedup',
					value: '2-3 orders',
					detail: 'vs baselines'
				},
				{
					label: 'Statistics',
					value: 'p < 0.001',
					detail: 'Kruskal-Wallis + Dunn-Bonferroni'
				}
			],
			figures: [
				{
					src: '/figures/benchmark_comparison_accuracy.png',
					alt: 'Benchmark comparison accuracy chart',
					caption:
						'SEW-Mimic achieves near-zero error*¹ (near machine precision tolerances) while remaining ~2 orders of magnitudes faster than xr_teleoperate (Unitree) and General Motion Retargeting.'
				}
			]
		},
		{
			id: 'safety-filter',
			title: 'Safety Filter',
			summary:
				'Our safety filter dramatically reduces self-collision of the robot.',
			citations: ['Paper Section VI-C', 'Figure 11', 'Appendix I'],
			metrics: [
				{
					label: 'Collision count',
					value: '316 -> 10 / 765',
					detail: 'without filter -> with filter'
				},
				{
					label: 'Collision rate',
					value: '41.3% -> 1.3%',
					detail: 'same retargeted sequence'
				},
				{
					label: 'Compute time',
					value: '~0.8 -> ~4.0 ms',
					detail: 'both arms'
				},
				{
					label: 'Alignment error (with filter)',
					value: '~0.019',
					detail: 'vs near-zero without filter'
				}
			],
			figures: [
				{
					src: '/figures/safety_filter_exp_plot_final.png',
					alt: 'Safety filter ablation chart',
					caption:
						'For the "rolling punch" motion*², the safety filter drastically reduces self-collision rate from 41.3% to 1.4%, while maintaining end-to-end inference rate of 250 Hz. The alignment error sees a small increase of 0.019.'
				}
			]
		},
		{
			id: 'user-study',
			title: 'Teleoperation User Study',
			summary: 'We compare the success of novice teleoperators (n=8) in difficult manipulation tasks.*³',
			citations: ['Paper Section VI-B', 'Figure 6', 'Table I', 'Appendix H'],
			// TODO: animated video of these tasks: Cabinet, Glass Gap, and Handover.
      metrics: [
				{
					label: 'Study size',
					value: 'N = 8 users',
					detail: '2x3 repeated-measures design'
				},
				{
					label: 'Total task successes',
					value: '125 vs 68',
					detail: 'SEW-Mimic vs MINK-IK'
				},
				{
					label: 'Success/failure interaction',
					value: 'p < 0.01',
					detail: 'ART ANOVA'
				},
				{
					label: 'Task effect',
					value: 'p < 0.05',
					detail: 'across dependent variables'
				}
			],
			figures: [
				{
					src: '/figures/glass_gap_eval_result.png',
					alt: 'Glass Gap evaluation success rate and completion time',
					caption:
						'For the Glass Gap task, users show 50.6% higher absolute success rate and 17.2% faster completion time when using SEW-Mimic.'
				},
				{
					src: '/figures/task_completion_time.png',
					alt: 'Task completion time comparison',
					caption:
						'Task completion times between SEW-Mimic and Mink across all tasks. SEW-Mimic shows 15.2% faster mean completion time, which translates to operational efficiency gains during large-scale teleoperation efforts.'
				}
			]
		},
		{
			id: 'policy-learning',
			title: 'Policy Learning Impact',
			summary: 'SEW-based action representation improves training results of autonomous task policies.',
			citations: ['Paper Section VI-D', 'Figure 12', 'Appendix J'],
			metrics: [
				{
					label: 'Demonstrations',
					value: '50 per dataset',
					detail: 'matched collection protocol'
				},
				{
					label: 'Evaluation',
					value: '250 initial states',
					detail: '3 training seeds'
				},
				{
					label: 'Policy success trend',
					value: '~3x higher',
					detail: 'SEW-collected demos (paper report)'
				},
				{
					label: 'Task completion',
					value: 'Lower with SEW',
					detail: 'consistent with Figure 12/appendix'
				}
			],
			figures: [
				{
					src: '/figures/rollout_comparison.png',
					alt: 'Policy rollout comparison',
					caption:
						'Policy rollout comparison for models trained on Mink-retargeted vs. SEW-Mimic-retargeted demonstrations.'
				},
				{
					src: '/figures/action_prediction_loss_bar.png',
					alt: 'Action prediction loss comparison',
					caption:
						'Action prediction error comparison for the trained imitation policies; lower error aligns with improved downstream task performance.'
				}
			]
		},
		{
			id: 'demo-quality',
			title: 'Demonstration Quality Diagnostics',
			summary:
				'SEW demonstrations show smoother command profiles and less aggressive velocity spikes.',
			citations: ['Figure 14', 'Figure 15', 'Figure 16', 'Appendix L'],
			metrics: [
				{
					label: 'Per-joint command velocity',
					value: 'Lower with SEW',
					detail: 'left and right arms'
				},
				{
					label: 'Smoothness (SPARC)',
					value: 'Higher with SEW',
					detail: 'smoother action trajectories'
				}
			],
			figures: [
				{
					src: '/figures/joint_vel_comparison.png',
					alt: 'Joint velocity comparison',
					caption:
						'Per-joint commanded velocity magnitudes for left and right arms; SEW-Mimic trajectories exhibit less aggressive velocity spikes.'
				},
				{
					src: '/figures/smoothness_per_joint.png',
					alt: 'Per-joint smoothness comparison',
					caption:
						'SPARC smoothness comparison per joint showing unanimously smoother action trajectories in demonstrations collected with SEW-Mimic.'
				}
			]
		}
	];
</script>

<main class="grid grid-cols-[1fr,min(80ch,100%),1fr] gap-y-8 py-12">
	<div class="col-start-2 px-4 text-center">
		<h1 class="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl lg:text-7xl/none">
			Benchmarks
		</h1>
	</div>

	<section class="col-span-3 bg-neutral-900 py-16 text-white">
		<div class="col-start-2 px-4">
			<div class="mx-auto max-w-6xl space-y-10">
				<div class="text-center">
					<h2
						class="text-xl font-semibold tracking-tighter sm:text-2xl md:text-3xl lg:text-4xl/none"
					>
						Interactive Retargeting Comparison
					</h2>
				</div>
				<div class="space-y-8">
					<div
						class="aspect-video overflow-hidden rounded-2xl border border-white/10 bg-black/40 shadow-lg"
					>
						{#if activeRecordingUrl}
							<iframe
								title={`Rerun viewer for ${activeSample?.title ?? 'motion sample'}`}
								src={rerunViewerUrl(activeRecordingUrl)}
								loading="lazy"
								allow="fullscreen; xr-spatial-tracking"
								class="h-full w-full"
							></iframe>
						{:else}
							<div
								class="flex h-full items-center justify-center px-6 text-center text-sm text-neutral-400"
							>
								Configure your R2 base URL to preview the capture here.
							</div>
						{/if}
					</div>
					<div class="space-y-4">
						{#if $debugEnabled && activeSample}
							<div
								class="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-neutral-200"
							>
								<p class="text-xs tracking-wide text-neutral-400 uppercase">Selected</p>
								<div class="mt-2 flex flex-wrap items-center justify-between gap-3">
									<p class="text-base font-semibold text-white">{activeSample.title}</p>
									{#if activeRecordingUrl}
										<a
											class="text-xs text-neutral-400 underline decoration-dotted underline-offset-4 transition-colors hover:text-neutral-200"
											href={resolveAssetUrl(activeRecordingUrl)}
											target="_blank"
											rel="noopener noreferrer"
										>
											Open source recording
										</a>
									{/if}
								</div>
							</div>
						{/if}
						<div class="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5">
							{#each motionSamples as sample (sample.key)}
								<Button
									variant={sample.key === activeSampleKey ? 'default' : 'ghost'}
									class={`w-full cursor-pointer justify-center border border-white/10 text-[0.7rem] tracking-wide uppercase ${
										sample.key === activeSampleKey
											? 'bg-white text-neutral-900 hover:bg-white/90'
											: 'bg-white/5 text-neutral-200 hover:bg-white/10 hover:text-white'
									}`}
									aria-pressed={sample.key === activeSampleKey}
									onclick={() => (activeSampleKey = sample.key)}
								>
									{sample.title}
								</Button>
							{/each}
						</div>
						{#if !hasR2BaseUrl}
							<p class="text-xs text-neutral-400">
								Set PUBLIC_RERUN_R2_BASE_URL to enable the Rerun viewer.
							</p>
						{/if}
					</div>
				</div>
			</div>
		</div>
	</section>

	{#each benchmarkSections as section (section.id)}
		<section class="col-start-2 px-4">
			<article
				class="group relative mx-auto max-w-6xl rounded-3xl border border-transparent p-6 transition-shadow duration-200 hover:border-neutral-200 hover:shadow-2xl dark:hover:border-neutral-700"
			>
				{#if section.citations && section.citations.length > 0}
					<div
						class="pointer-events-none absolute top-full left-1/2 z-10 mt-3 w-max max-w-[90vw] -translate-x-1/2 opacity-0 transition-opacity duration-150 group-hover:opacity-100"
					>
						<div
							class="flex flex-wrap justify-center gap-2 rounded-md border border-neutral-300 bg-white/95 p-2 shadow-sm dark:border-neutral-700 dark:bg-neutral-900/95"
						>
							{#each orderedCitations(section.citations) as citation}
								<span
									class="rounded-full border border-neutral-300 bg-neutral-100 px-2.5 py-1 text-[0.68rem] tracking-wide text-neutral-600 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300"
								>
									{citation}
								</span>
							{/each}
						</div>
					</div>
				{/if}
				<header class="mb-2 flex flex-col items-center gap-2">
					<div class="inline-flex flex-col items-center">
						<h2
							class="text-center text-xl font-semibold tracking-tighter text-neutral-900 sm:text-2xl md:text-3xl lg:text-4xl/none dark:text-neutral-100"
						>
							{section.title}
						</h2>
					</div>
					<p
						class="max-w-3xl text-center text-lg leading-relaxed text-neutral-800 dark:text-neutral-200"
					>
						{section.summary}
					</p>
				</header>

				<div
					class={`mt-6 grid gap-6 ${
						section.id === 'demo-quality' || section.id === 'full-body' ? 'grid-cols-1' : 'lg:grid-cols-2'
					}`}
				>
					{#each section.figures as figure, figureIndex (figure.src)}
						<figure
							class={`bg-transparent p-0 ${
								section.figures.length % 2 === 1 && figureIndex === section.figures.length - 1
									? 'lg:col-span-2 lg:mx-auto lg:w-full lg:max-w-4xl'
									: ''
							}`}
						>
							<img
								src={resolveAssetUrl(figure.src)}
								alt={figure.alt}
								loading="lazy"
								class="max-h-[420px] w-full bg-white object-contain"
							/>
							<figcaption
								class="pt-3 text-base leading-relaxed text-neutral-700 dark:text-neutral-300"
							>
								{figure.caption}
							</figcaption>
						</figure>
					{/each}
				</div>

				{#if $showInfoCard}
					<div class="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
						{#each section.metrics as metric}
							<div
								class="rounded-xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-700 dark:bg-neutral-900/60"
							>
								<p class="text-xs tracking-wide text-neutral-500 uppercase dark:text-neutral-400">
									{metric.label}
								</p>
								<p class="mt-1 text-lg font-semibold text-neutral-900 dark:text-neutral-100">
									{metric.value}
								</p>
								{#if metric.detail}
									<p class="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{metric.detail}</p>
								{/if}
							</div>
						{/each}
					</div>
				{/if}

				{#if $showInfoCard && section.notes && section.notes.length > 0}
					<div
						class="mt-5 rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-xs text-neutral-600 dark:border-neutral-700 dark:bg-neutral-900/60 dark:text-neutral-300"
					>
						<p
							class="mb-2 font-semibold tracking-wide text-neutral-500 uppercase dark:text-neutral-400"
						>
							Notes
						</p>
						<ul class="list-disc space-y-1 pl-5">
							{#each section.notes as note}
								<li>{note}</li>
							{/each}
						</ul>
					</div>
				{/if}
			</article>
		</section>
	{/each}
</main>
